import React, { useContext } from "react";
import "./testScenariosScreen.css";
import { DataLayerContext } from "../dataLayer/DataLayer";
import { AppBar } from "./components/appBar";
import { PageHeading } from "./components/pageHeading";
import { Check, ChevronRight } from "./components/icons";
import { getTestScenarios, type ITestScenario } from "../testMode";
import { useObservableProperty } from "../observable/useObservableProperty";

interface TestScenarioGroup {
  id: string;
  title: string;
  description: string;
  scenarioIds: string[];
}

interface TestScenarioPickerPosition {
  scrollTop: number;
  anchorScenarioId: string | null;
  anchorOffset: number;
}

let savedPickerPosition: TestScenarioPickerPosition | null = null;

function capturePickerPosition(content: HTMLDivElement): void {
  const contentTop = content.getBoundingClientRect().top;
  const cards = Array.from(content.querySelectorAll<HTMLButtonElement>("[data-scenario-id]"));
  const anchor =
    cards.find((card) => card.getBoundingClientRect().bottom > contentTop + 1) ??
    cards[cards.length - 1];
  savedPickerPosition = {
    scrollTop: content.scrollTop,
    anchorScenarioId: anchor?.dataset.scenarioId ?? null,
    anchorOffset: anchor ? anchor.getBoundingClientRect().top - contentTop : 0,
  };
}

function restorePickerPosition(content: HTMLDivElement): void {
  if (!savedPickerPosition) {
    content.scrollTop = 0;
    return;
  }
  const saved = savedPickerPosition;
  const anchor = Array.from(content.querySelectorAll<HTMLButtonElement>("[data-scenario-id]")).find(
    (card) => card.dataset.scenarioId === saved.anchorScenarioId,
  );
  const desired = anchor
    ? content.scrollTop +
      anchor.getBoundingClientRect().top -
      content.getBoundingClientRect().top -
      saved.anchorOffset
    : saved.scrollTop;
  const maxScrollTop = Math.max(0, content.scrollHeight - content.clientHeight);
  content.scrollTop = Math.max(0, Math.min(desired, maxScrollTop));
}

function keepPickerItemVisible(content: HTMLDivElement, item: HTMLButtonElement | null): void {
  if (!item) return;
  const contentBounds = content.getBoundingClientRect();
  const itemBounds = item.getBoundingClientRect();
  const margin = 16;
  let adjustment = 0;
  if (itemBounds.top < contentBounds.top + margin) {
    adjustment = itemBounds.top - contentBounds.top - margin;
  } else if (itemBounds.bottom > contentBounds.bottom - margin) {
    adjustment = itemBounds.bottom - contentBounds.bottom + margin;
  }
  if (adjustment === 0) return;
  const maxScrollTop = Math.max(0, content.scrollHeight - content.clientHeight);
  content.scrollTop = Math.max(0, Math.min(content.scrollTop + adjustment, maxScrollTop));
}

const SCENARIO_GROUPS: TestScenarioGroup[] = [
  {
    id: "membership-acquisition",
    title: "Membership acquisition & confirmation",
    description: "Joining, return detection, Patreon propagation, retries, and purchase intent.",
    scenarioIds: [
      "premium-settings-nonbacker",
      "premium-settings-linked-nonmember",
      "premium-link-complete-no-tier",
      "premium-delayed-propagation",
      "premium-purchase-unlinked",
      "premium-purchase-intent-restore",
      "premium-purchase-intent-other-account",
      "premium-subscription-retry-feedback",
      "premium-purchase-retries-exhausted",
      "premium-return-verification-error",
      "premium-purchase-error-retries-exhausted",
      "premium-complimentary-patreon-pending",
    ],
  },
  {
    id: "membership-celebrations",
    title: "Celebrations & upgrades",
    description: "New-member Bronze/Silver/Gold and upgrade-to-Silver/Gold experiences.",
    scenarioIds: [
      "premium-celebration-new",
      "premium-celebration-silver-preview",
      "premium-celebration-preview",
      "premium-celebration-upgrade",
      "premium-celebration-gold-upgrade",
    ],
  },
  {
    id: "premium-access",
    title: "Premium access & errors",
    description:
      "Leaderboard, stats, Settings, complimentary access, errors, and repeat prevention.",
    scenarioIds: [
      "premium-leaderboard-backer",
      "premium-leaderboard-nonbacker",
      "premium-leaderboard-refresh-granted",
      "premium-leaderboard-refresh-failed",
      "premium-leaderboard-verification-error",
      "stats-today-records",
      "premium-stats-backer",
      "premium-stats-nonbacker",
      "premium-settings-backer",
      "premium-complimentary-grant",
      "premium-celebration-acknowledged",
      "premium-existing-entitlement-error",
    ],
  },
  {
    id: "home-indicators",
    title: "Home premium indicators",
    description: "Bronze, Silver, Gold, neutral, loading, and verification-error stars.",
    scenarioIds: [
      "premium-home-bronze",
      "premium-home-silver",
      "premium-home-gold",
      "premium-home-nonbacker",
      "premium-home-loading",
      "premium-home-verification-error",
    ],
  },
  {
    id: "account-switching",
    title: "Account switching",
    description: "Account-scoped membership acknowledgement and repeat-prevention sequence.",
    scenarioIds: ["premium-account-a-new", "premium-account-b-new", "premium-account-a-return"],
  },
  {
    id: "gameplay",
    title: "Gameplay & puzzle states",
    description: "Loading, playing, Home history, review, and delayed daily-puzzle states.",
    scenarioIds: [
      "connecting",
      "today-playing",
      "today-expired-home",
      "today-played-home",
      "today-played-review",
      "home-history",
      "today-loading",
      "today-not-ready",
      "review-past",
      "review-past-pending",
    ],
  },
  {
    id: "social-operations",
    title: "Social & operations",
    description: "Friend-invite entry and the private operations dashboard fixture.",
    scenarioIds: ["invite-follow", "operations-dashboard"],
  },
];

function groupScenarios(scenarios: ITestScenario[]): Array<{
  group: TestScenarioGroup;
  scenarios: ITestScenario[];
}> {
  const byId = new Map(scenarios.map((scenario) => [scenario.id, scenario]));
  const grouped = new Set(SCENARIO_GROUPS.flatMap((group) => group.scenarioIds));
  const groups = SCENARIO_GROUPS.map((group) => ({
    group,
    scenarios: group.scenarioIds
      .map((scenarioId) => byId.get(scenarioId))
      .filter((scenario): scenario is ITestScenario => scenario !== undefined),
  })).filter(({ scenarios: groupItems }) => groupItems.length > 0);
  const other = scenarios.filter((scenario) => !grouped.has(scenario.id));

  if (other.length > 0) {
    groups.push({
      group: {
        id: "other",
        title: "Other scenarios",
        description: "Additional fixtures not yet assigned to a dedicated group.",
        scenarioIds: other.map((scenario) => scenario.id),
      },
      scenarios: other,
    });
  }
  return groups;
}

/**
 * Test-mode landing page (reached via `?test`). Lists canned game states so we
 * can jump straight into a specific scenario with test data — not shipped to
 * real users, who never hit test mode.
 */
export function TestScenariosScreen() {
  const dataLayer = useContext(DataLayerContext);
  const currentScenarioId = useObservableProperty(dataLayer.currentTestScenarioId);
  const groups = groupScenarios(getTestScenarios());
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    restorePickerPosition(content);
    const currentScenario = content.querySelector<HTMLButtonElement>('[aria-current="true"]');
    currentScenario?.focus({ preventScroll: true });
    // WebKit can ignore preventScroll when focus returns to a remounted grid.
    // Reapply the saved anchor synchronously so keyboard focus never moves the
    // picker away from the viewport the tester just left.
    restorePickerPosition(content);
    keepPickerItemVisible(content, currentScenario);
    capturePickerPosition(content);

    const onScroll = () => capturePickerPosition(content);
    content.addEventListener("scroll", onScroll, { passive: true });

    let previousWidth = content.clientWidth;
    let previousHeight = content.clientHeight;
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            if (content.clientWidth === previousWidth && content.clientHeight === previousHeight) {
              return;
            }
            previousWidth = content.clientWidth;
            previousHeight = content.clientHeight;
            restorePickerPosition(content);
            keepPickerItemVisible(
              content,
              content.querySelector<HTMLButtonElement>('[aria-current="true"]'),
            );
            capturePickerPosition(content);
          });
    resizeObserver?.observe(content);

    return () => {
      capturePickerPosition(content);
      content.removeEventListener("scroll", onScroll);
      resizeObserver?.disconnect();
    };
  }, []);

  return (
    <div className="screen testscn">
      <AppBar brand />
      <PageHeading
        eyebrow="Developer"
        title="Test scenarios"
        sub="Choose a deterministic app state. Related fixtures are grouped for faster review."
      />
      <div className="testscn__content" ref={contentRef}>
        <div className="testscn__groups">
          {groups.map(({ group, scenarios }) => {
            const headingId = `testscn-group-${group.id}`;
            return (
              <section className="testscn__group" aria-labelledby={headingId} key={group.id}>
                <div className="testscn__group-head">
                  <h2 id={headingId}>{group.title}</h2>
                  <p>{group.description}</p>
                </div>
                <div className="testscn__grid">
                  {scenarios.map((scenario) => {
                    const current = currentScenarioId === scenario.id;
                    return (
                      <button
                        key={scenario.id}
                        className={`testscn__row${current ? " testscn__row--current" : ""}`}
                        data-scenario-id={scenario.id}
                        aria-current={current ? "true" : undefined}
                        onClick={() => {
                          if (contentRef.current) capturePickerPosition(contentRef.current);
                          dataLayer.loadTestScenario(scenario.id);
                        }}
                      >
                        <div className="testscn__text">
                          <div className="testscn__label">{scenario.label}</div>
                          <div className="testscn__desc">{scenario.description}</div>
                        </div>
                        {current ? (
                          <span className="testscn__current">
                            <Check />
                            Current
                          </span>
                        ) : (
                          <ChevronRight className="testscn__chev" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
        <div className="testscn__foot">
          Add <code>?test=0</code> to the URL to leave test mode.
        </div>
      </div>
    </div>
  );
}
